import { logger } from '@tunasong/models'
import { NoteLib } from '@tunasong/music-lib'
import type { ComplementaryScales } from '@tunasong/music-lib'
import type { DesignTokens } from '@tunasong/ui-lib'

/** Simple contrast text: return white for dark colors, black for light */
const getContrastText = (hex: string): string => {
  const c = hex.replace('#', '')
  const r = parseInt(c.substring(0, 2), 16)
  const g = parseInt(c.substring(2, 4), 16)
  const b = parseInt(c.substring(4, 6), 16)
  // W3C relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#000' : '#fff'
}

export const drawCircleOfFifths = ({
  ctx,
  tokens,
  height: outerHeight,
  width: outerWidth,
  circleOfFifths,
}: {
  ctx: CanvasRenderingContext2D
  tokens: DesignTokens
  height: number
  width: number
  circleOfFifths: ComplementaryScales[]
}) => {
  const padding = 16
  const width = outerWidth - 2 * padding

  if (width <= 0 || outerHeight <= 0) {
    logger.debug(`Canvas size too small: ${width}x${outerHeight}, skipping draw`)
    return
  }

  const radius = width / 2
  const innerRadius = width / 3
  const centerX = outerWidth / 2
  const centerY = outerHeight / 2

  const radialLabelColor = tokens.palette.divider
  const innerCircleColor = tokens.palette.primary.main
  const innerCircleTextColor = tokens.palette.primary.contrastText
  const outerCircleColor = tokens.palette.secondary.main
  const outerCircleTextColor = tokens.palette.secondary.contrastText
  const pieFillColor = tokens.palette.secondary.light
  const pieStrokeColor = tokens.palette.background.default
  const textColor = tokens.palette.text.primary

  const drawRadialLabel = (
    label: string,
    fontSize: number,
    angle: number,
    r: number,
    color: string = radialLabelColor,
    circleColor?: string
  ) => {
    const cos = Math.cos(angle)
    const sin = Math.sin(angle)

    ctx.font = `${fontSize}px courier`
    const labelSize = ctx.measureText(label)

    const offsetX1 = cos * r - labelSize.width / 2
    const offsetY1 = sin * r + fontSize / 2

    // Draw filled circle behind label
    ctx.beginPath()
    if (circleColor) {
      const circleRadius = Math.max(labelSize.width, fontSize) / 2 + 2
      ctx.arc(centerX + cos * r, centerY + sin * r, circleRadius, 0, Math.PI * 2)
      ctx.fillStyle = circleColor
      ctx.fill()
    }

    ctx.beginPath()
    // Draw label text
    ctx.fillStyle = circleColor ? getContrastText(circleColor) : color
    ctx.fillText(label, centerX + offsetX1, centerY + offsetY1 - 3)
  }

  // Pie chart - http://www.codeblocq.com/2016/04/Create-a-Pie-Chart-with-HTML5-canvas/
  // Hover effects - https://stackoverflow.com/questions/29300280/update-html5-canvas-rectangle-on-hover
  /** Outer pie */
  ctx.beginPath()
  ctx.fillStyle = outerCircleColor
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, true) // Outer circle
  ctx.fill()

  /** Inner slice */
  ctx.beginPath()
  ctx.fillStyle = ctx.fillStyle = innerCircleColor
  ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2, true)
  ctx.fill()

  /** center circle */
  ctx.beginPath()
  ctx.fillStyle = ctx.fillStyle = tokens.palette.background.default
  ctx.arc(centerX, centerY, innerRadius / 2, 0, Math.PI * 2, true)
  ctx.fill()

  const rad = (2 * Math.PI) / 12
  for (let i = 1; i <= 12; i++) {
    /** Start "at the top" */
    const keys = circleOfFifths[(i + 3 - 1) % 12]
    /** The slices - we center them */
    const startAngle = (i - 1) * rad - rad / 2
    const endAngle = startAngle + rad
    const midAngle = startAngle + (endAngle - startAngle) / 2

    ctx.beginPath()
    ctx.moveTo(centerX, centerY)

    ctx.fillStyle = pieFillColor
    ctx.strokeStyle = pieStrokeColor
    ctx.lineWidth = 2
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, false)
    ctx.lineTo(centerX, centerY)

    const largeFont = Math.round(width / 10)
    const smallFont = Math.round(width / 20)
    const outerDegreeFont = Math.round(width / 18)
    const innerDegreeFont = Math.round(width / 25)

    const majorTextRad = innerRadius + (radius - innerRadius) / 2.2
    const majorDegreeTextRad = radius
    const minorTextRad = innerRadius - innerRadius / 5
    const minorDegreeTextRad = innerRadius - smallFont * 3.5
    ctx.stroke()

    const degreeColor = tokens.palette.grey['300']
    const degreeColorActive = tokens.palette.success.main

    drawRadialLabel(NoteLib.prettifyNote(keys.major), largeFont, midAngle, majorTextRad, outerCircleTextColor)
    if (keys.majorDegreeName) {
      drawRadialLabel(
        keys.majorDegreeName,
        outerDegreeFont,
        midAngle,
        majorDegreeTextRad,
        textColor,
        keys.majorDegree === 'I' ? degreeColorActive : degreeColor
      )
    }
    drawRadialLabel(NoteLib.prettifyNote(`${keys.minor}m`), smallFont, midAngle, minorTextRad, innerCircleTextColor)
    if (keys.minorDegreeName) {
      drawRadialLabel(
        keys.minorDegreeName,
        innerDegreeFont,
        midAngle,
        minorDegreeTextRad,
        tokens.palette.text.primary,
        keys.minorDegree === 'I' ? degreeColorActive : degreeColor
      )
    }

    ctx.stroke()
  }
}
